import { useState, useEffect } from 'react';
import { Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import { GitHubConfig } from './components/GitHubConfig';
import { PromptList } from './components/PromptList';
import { NewPromptPage } from './components/NewPromptPage';
import { GitHubPromptManager } from './utils/github';
import { GitHubConfig as GitHubConfigType, Prompt } from './types';
import { useGitHubAuth } from './hooks/useGitHubAuth';
import GitHubLogin from './components/GitHubLogin';
import PromptDetail from './components/PromptDetail';
import { ComparisonPage } from './components/ComparisonPage';
import { persistNewPrompt } from './utils/promptSave';

const MainApp = () => {
  const { isAuthenticated, user, logout, isLoading } = useGitHubAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [config, setConfig] = useState<GitHubConfigType | null>(null);
  const [manager, setManager] = useState<GitHubPromptManager | null>(null);
  const [selectedPrompt, setSelectedPrompt] = useState<Prompt | null>(null);
  const [promptListKey, setPromptListKey] = useState(0);

  useEffect(() => {
    if (isAuthenticated) {
      // Load config from localStorage or create default
      const savedConfig = localStorage.getItem('github-config');
      if (savedConfig) {
        try {
          const parsedConfig = JSON.parse(savedConfig);
          // Remove token from config since we use BFF now
          const { token, ...configWithoutToken } = parsedConfig;
          // Token is ignored in BFF pattern
          void token;
          setConfig(configWithoutToken);
          setManager(new GitHubPromptManager(configWithoutToken));
        } catch (error) {
          console.error('Failed to parse saved config:', error);
          localStorage.removeItem('github-config');
        }
      } else {
        // Create default config for first-time OAuth users
        const defaultConfig = {
          owner: 'rjmette', // Default to your username
          repo: 'prompt-lib-a'
        };
        setConfig(defaultConfig);
        setManager(new GitHubPromptManager(defaultConfig));
        localStorage.setItem('github-config', JSON.stringify(defaultConfig));
      }
    }
  }, [isAuthenticated]);

  const handleConfigSave = (newConfig: GitHubConfigType) => {
    // Remove token from config since we use BFF now
    const { token, ...configWithoutToken } = newConfig;
    // Token is ignored in BFF pattern
    void token;
    setConfig(configWithoutToken);
    setManager(new GitHubPromptManager(configWithoutToken));
    localStorage.setItem('github-config', JSON.stringify(configWithoutToken));
  };

  const handleSelectPrompt = (prompt: Prompt) => {
    setSelectedPrompt(prompt);
  };

  const handleCreateNew = () => {
    navigate('/prompts/new');
  };

  const handleBackToLibrary = () => {
    navigate('/');
    setSelectedPrompt(null);
  };

  const handleSaveNewPrompt = async (prompt: Prompt) => {
    if (!manager) {
      throw new Error('GitHub is not configured. Please try again.');
    }

    // Await persistence; errors propagate to NewPromptPage for inline retry.
    // Navigate/reset only after this resolves successfully.
    await persistNewPrompt(async (toSave) => {
      const fileName = `${toSave.metadata.category}/${toSave.metadata.id}.yaml`;
      await manager.savePrompt(fileName, toSave);
      console.log('Prompt saved successfully:', fileName);
    }, prompt);

    navigate('/');
    setPromptListKey(prev => prev + 1);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <GitHubLogin />;
  }

  if (!config || !manager) {
    return (
      <div className="min-h-screen bg-gray-50">
        <GitHubConfig onConfigSave={handleConfigSave} />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header with profile */}
      {isAuthenticated && user && (
        <header className="bg-white shadow-sm border-b">
          <div className="container mx-auto px-4 py-3 flex justify-between items-center">
            <h1 className="text-xl font-semibold text-gray-900">Prompt Manager</h1>
            <div className="relative group">
              <button className="flex items-center space-x-2 p-2 rounded-lg hover:bg-gray-100 transition-colors">
                <img 
                  src={user.avatar_url} 
                  alt={user.name || user.login}
                  className="w-8 h-8 rounded-full"
                />
                <span className="text-sm font-medium text-gray-700">{user.name || user.login}</span>
                <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              
              {/* Dropdown menu */}
              <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg border border-gray-200 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-10">
                <div className="py-1">
                  <div className="px-4 py-2 text-sm text-gray-500 border-b">
                    Signed in as<br />
                    <span className="font-medium text-gray-900">{user.login}</span>
                  </div>
                  <button
                    onClick={logout}
                    className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
                  >
                    Sign out
                  </button>
                </div>
              </div>
            </div>
          </div>
        </header>
      )}
      
      {/* Route-based content */}
      {location.pathname === '/compare' ? (
        <div className="container mx-auto px-4 py-8"><ComparisonPage manager={manager} config={config} onBack={handleBackToLibrary} /></div>
      ) : location.pathname === '/prompts/new' ? (
        <NewPromptPage
          manager={manager}
          onBack={handleBackToLibrary}
          onSave={handleSaveNewPrompt}
        />
      ) : (
        <div className="container mx-auto px-4 py-8">
          {selectedPrompt ? (
          <PromptDetail
            prompt={selectedPrompt}
            onBack={() => setSelectedPrompt(null)}
          />
        ) : (
          <PromptList
            key={promptListKey}
            manager={manager}
            onSelectPrompt={handleSelectPrompt}
            onCreateNew={handleCreateNew}
            onCompare={() => navigate('/compare')}
          />
        )}
        </div>
      )}
    </div>
  );
};

function App() {
  return (
    <Routes>
      <Route path="*" element={<MainApp />} />
    </Routes>
  );
}

export default App;
